import { Chart, registerables } from 'chart.js';
import 'chartjs-adapter-date-fns';
import React, { useEffect, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { standardLegend, standardTitle } from '../../utils/chartFormatting';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { getVMTDataForGHG } from '../../services/vmtDataService';
import './VMTComparisonChart.css';

Chart.register(...registerables);

interface VMTComparisonChartProps {
  location: string;
  geofips: number | null;
}

interface VMTDataPoint {
  year: number;
  ldv: number;
  hdv: number;
  total: number;
}

export const VMTComparisonChart: React.FC<VMTComparisonChartProps> = ({ location, geofips }) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<VMTDataPoint[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!location || !geofips) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Fetch LDV VMT from VMT chart data (uses correct methodology: Replica 2023, AADT backcast 2015-2022, EMFAC backcast 2005-2014)
        // NOTE: This uses the SAME calculated data as the VMT Chart (first chart) via getVMTDataForGHG()
        // which returns cached data from setVMTDataFromTransportation() called by the VMT Chart
        console.log('🔍 VMT Comparison: Fetching LDV VMT from VMT chart data for', location);
        console.log('🔍 VMT Comparison: This uses the SAME calculated data as the VMT Chart (first chart)');
        const vmtChartData = await getVMTDataForGHG(location, geofips || undefined);
        console.log('✅ VMT Comparison: LDV VMT data from VMT chart:', vmtChartData);
        console.log('✅ VMT Comparison: LDV VMT data years:', vmtChartData.map(d => d.year).sort((a, b) => a - b));
        console.log('✅ VMT Comparison: LDV VMT data values:', vmtChartData.map(d => ({ 
          year: d.year, 
          vmt: d.vmt?.toLocaleString(), 
          source: d.source,
          method_note: d.method_note 
        })));
        
        // Check if 2022 is missing and log why
        const has2022 = vmtChartData.some(d => d.year === 2022);
        if (!has2022) {
          console.warn('⚠️ VMT Comparison: 2022 is MISSING from VMT Chart data. This means the VMT Chart did not calculate 2022.');
          console.warn('⚠️ VMT Comparison: This could be due to missing AADT data for 2022, or an issue in the VMT Chart calculation.');
        }
        
        // Validate VMT data - check for missing or suspicious values
        if (vmtChartData.length === 0) {
          throw new Error('No LDV VMT data available');
        }
        
        // Check if 2022 is missing and backcast it from 2023 using AADT (same as GHG inventory)
        const vmt2022 = vmtChartData.find(d => d.year === 2022);
        const vmt2023 = vmtChartData.find(d => d.year === 2023);
        
        if ((!vmt2022 || vmt2022.vmt === 0) && vmt2023 && vmt2023.vmt > 0 && geofips) {
          console.log('⚠️ VMT Comparison: VMT for 2022 is missing or zero, backcasting from 2023 using AADT...');
          try {
            // Fetch AADT data for 2022 (only need 2022 for backcasting from 2023)
            const { data: aadtData, error: aadtError } = await supabaseClient
              .from('aadt_filtered')
              .select('year, value')
              .eq('geofips', geofips)
              .eq('year', 2022)
              .single();
            
            // Try location_name fallback if geofips didn't work
            let aadtValue: number | null = null;
            if (!aadtError && aadtData && aadtData.value) {
              aadtValue = aadtData.value;
            } else {
              // Try location_name fallback
              const { data: aadtByName, error: aadtByNameError } = await supabaseClient
                .from('aadt_filtered')
                .select('year, value')
                .eq('location_name', location)
                .eq('year', 2022)
                .single();
              
              if (!aadtByNameError && aadtByName && aadtByName.value) {
                aadtValue = aadtByName.value;
              }
            }
            
            if (aadtValue !== null) {
              // Backcast: VMT_2022 = VMT_2023 × AADT_2022
              // AADT values are scaling factors: VMT_year = VMT_previous_year × AADT_year
              const backcastedVMT2022 = Math.round(vmt2023.vmt * aadtValue);
              console.log(`✅ VMT Comparison: Backcasted 2022 VMT from 2023: ${backcastedVMT2022.toLocaleString()} (2023 VMT: ${vmt2023.vmt.toLocaleString()}, AADT 2022: ${aadtValue})`);
              
              // Add or update 2022 VMT in the data
              const vmt2022Index = vmtChartData.findIndex(d => d.year === 2022);
              if (vmt2022Index >= 0) {
                vmtChartData[vmt2022Index].vmt = backcastedVMT2022;
                vmtChartData[vmt2022Index].source = 'vmt_chart_backcasted';
                vmtChartData[vmt2022Index].method_note = 'VMT data from VMT Chart, 2022 backcasted from 2023 using AADT';
              } else {
                vmtChartData.push({
                  year: 2022,
                  vmt: backcastedVMT2022,
                  source: 'vmt_chart_backcasted',
                  method_note: 'VMT data backcasted from 2023 using AADT'
                });
                // Sort by year
                vmtChartData.sort((a, b) => a.year - b.year);
              }
            } else {
              console.warn('⚠️ VMT Comparison: AADT data for 2022 not found, cannot backcast:', aadtError);
            }
          } catch (error) {
            console.warn('⚠️ VMT Comparison: Error backcasting 2022 VMT:', error);
          }
        }
        
        // Detailed analysis for suspicious values
        const suspiciousYears = vmtChartData.filter(d => d.vmt === 0 || d.vmt < 1000);
        if (suspiciousYears.length > 0) {
          console.warn(`⚠️ VMT Comparison: Found ${suspiciousYears.length} suspicious LDV values:`, suspiciousYears.map(d => ({
            year: d.year,
            vmt: d.vmt,
            source: d.source,
            method_note: d.method_note
          })));
        }
        
        // Get HDV VMT from emfac2025_fleet
        // ALWAYS use geofips (not location_name) to avoid duplicates
        if (!geofips) {
          throw new Error('Geofips is required for HDV VMT data');
        }
        
        console.log('🔍 VMT Comparison: Fetching HDV data using geofips:', geofips);
        // First, let's see what columns exist in the table by fetching all columns for one row
        const { data: sampleRow } = await supabaseClient
          .from('emfac2025_fleet')
          .select('*')
          .eq('geofips', geofips)
          .eq('class', 'HDV')
          .eq('year', 2023)
          .limit(1)
          .single();
        
        if (sampleRow) {
          console.log('🔍 VMT Comparison: Sample HDV row structure (all columns):', Object.keys(sampleRow));
          console.log('🔍 VMT Comparison: Sample HDV row values:', sampleRow);
        }
        
        // Determine geography_type based on location (city vs county)
        // For cities, use 'City'; for counties, use 'County'
        // We can infer this from the geofips: city geofips are typically 6 digits, county geofips are 5 digits
        // But to be safe, we'll query for both and use the one that returns data
        let hdvData: any[] = [];
        let hdvError: any = null;
        
        // Try City first (most common for city locations)
        const { data: cityData, error: cityError } = await supabaseClient
          .from('emfac2025_fleet')
          .select('year, total_vmt, geofips, location_name, geography_type')
          .eq('geofips', geofips)
          .eq('geography_type', 'City')
          .eq('class', 'HDV')
          .gte('year', 2015)
          .lte('year', 2023)
          .order('year', { ascending: true });
        
        if (!cityError && cityData && cityData.length > 0) {
          hdvData = cityData;
          console.log('✅ VMT Comparison: Using City geography_type for HDV data');
        } else {
          // Try County as fallback
          const { data: countyData, error: countyError } = await supabaseClient
            .from('emfac2025_fleet')
            .select('year, total_vmt, geofips, location_name, geography_type')
            .eq('geofips', geofips)
            .eq('geography_type', 'County')
            .eq('class', 'HDV')
            .gte('year', 2015)
            .lte('year', 2023)
            .order('year', { ascending: true });
          
          if (!countyError && countyData && countyData.length > 0) {
            hdvData = countyData;
            console.log('✅ VMT Comparison: Using County geography_type for HDV data');
          } else {
            hdvError = countyError || cityError;
            console.warn('⚠️ VMT Comparison: No HDV data found for City or County geography_type');
          }
        }
        
        if (hdvError) {
          console.error('Error fetching HDV data:', hdvError);
          throw hdvError;
        }
        
        if (!hdvData || hdvData.length === 0) {
          console.warn('⚠️ VMT Comparison: No HDV data found for geofips:', geofips);
        } else {
          console.log('✅ VMT Comparison: HDV data found using geofips:', geofips, 'count:', hdvData.length);
        }

        console.log('📊 VMT Comparison: HDV data count:', hdvData?.length);
        if (hdvData && hdvData.length > 0) {
          console.log('📊 VMT Comparison: First HDV row:', JSON.stringify(hdvData[0], null, 2));
          console.log('📊 VMT Comparison: Sample HDV rows (first 5):', hdvData.slice(0, 5).map(d => ({ year: d.year, total_vmt: d.total_vmt, geofips: d.geofips })));
          const uniqueYears = Array.from(new Set(hdvData.map(d => d.year)));
          console.log('📊 VMT Comparison: Unique years in HDV data:', uniqueYears.length, uniqueYears.sort());
          
          // Log detailed breakdown by year to investigate anomalies
          uniqueYears.forEach(year => {
            const yearData = hdvData.filter(d => d.year === year);
            const sum = yearData.reduce((acc, d) => acc + (d.total_vmt || 0), 0);
            const avg = yearData.length > 0 ? sum / yearData.length : 0;
            const min = Math.min(...yearData.map(d => d.total_vmt || 0));
            const max = Math.max(...yearData.map(d => d.total_vmt || 0));
            
            console.log(`📊 VMT Comparison: HDV ${year}: ${yearData.length} entries, sum=${sum.toLocaleString()}, avg=${avg.toLocaleString()}, min=${min.toLocaleString()}, max=${max.toLocaleString()}`);
            
            // For 2023 specifically, log all entries to investigate the spike
            if (year === 2023) {
              console.log(`🔍 VMT Comparison: HDV 2023 detailed breakdown:`, yearData.map((d, idx) => ({
                index: idx,
                total_vmt: d.total_vmt,
                geofips: d.geofips,
                location_name: d.location_name || 'N/A'
              })));
              
              // Check for potential duplicates or data quality issues
              const geofipsCounts = new Map<number, number>();
              yearData.forEach(d => {
                const g = d.geofips;
                geofipsCounts.set(g, (geofipsCounts.get(g) || 0) + 1);
              });
              const duplicateGeofips = Array.from(geofipsCounts.entries()).filter(([g, count]) => count > 1);
              if (duplicateGeofips.length > 0) {
                console.warn(`⚠️ VMT Comparison: HDV 2023 has duplicate geofips:`, duplicateGeofips);
              }
            }
          });
        }

        // Process HDV data by year
        const hdvByYear = new Map<number, number>();
        
        // Sum HDV VMT for 2015-2023 from emfac2025_fleet
        (hdvData || []).forEach(row => {
          hdvByYear.set(row.year, (hdvByYear.get(row.year) || 0) + (row.total_vmt || 0));
        });
        
        console.log('📊 VMT Comparison: HDV by year (before smoothing):', Array.from(hdvByYear.entries()).map(([year, vmt]) => ({ year, vmt })));
        
        // Apply data smoothing to detect and fix anomalies (similar to GHG inventory methodology)
        // If year-over-year change > 50%, use previous year's value instead
        const sortedYears = Array.from(hdvByYear.keys()).sort((a, b) => a - b);
        const smoothedHdvByYear = new Map<number, number>();
        
        sortedYears.forEach((year, index) => {
          const currentValue = hdvByYear.get(year) || 0;
          
          if (index === 0) {
            // First year: use as-is
            smoothedHdvByYear.set(year, currentValue);
          } else {
            const previousYear = sortedYears[index - 1];
            const previousValue = smoothedHdvByYear.get(previousYear) || 0;
            
            // Calculate year-over-year change percentage
            const changePercent = previousValue > 0 
              ? Math.abs(currentValue - previousValue) / previousValue 
              : 1;
            
            if (changePercent > 0.5) {
              // Anomaly detected: use previous year's value
              console.warn(`⚠️ VMT Comparison: HDV ${year} anomaly detected (${(changePercent * 100).toFixed(1)}% change from ${previousYear}). Using ${previousYear} value: ${previousValue.toLocaleString()} instead of ${currentValue.toLocaleString()}`);
              smoothedHdvByYear.set(year, previousValue);
            } else {
              // Normal change: use current value
              smoothedHdvByYear.set(year, currentValue);
            }
          }
        });
        
        // Replace original map with smoothed values
        hdvByYear.clear();
        smoothedHdvByYear.forEach((value, year) => {
          hdvByYear.set(year, value);
        });
        
        console.log('📊 VMT Comparison: HDV by year (after smoothing):', Array.from(hdvByYear.entries()).map(([year, vmt]) => ({ year, vmt })));
        
        // Get 2015 HDV VMT as baseline for backcasting (use smoothed value)
        const hdv2015 = hdvByYear.get(2015) || 0;
        console.log('📊 VMT Comparison: HDV 2015 baseline (smoothed):', hdv2015);
        
        // Resolve county name for EMFAC backcasting ratios
        await supabaseLocationService.initialize().catch(() => {});
        const resolvedCounty = (supabaseLocationService.getCountyForCity(location) || location).trim();
        const baseCountyName = resolvedCounty.replace(/\s+County$/i, '');
        const countyVariations = Array.from(new Set([
          `${baseCountyName} County`,
          baseCountyName,
          resolvedCounty
        ]));

        let emfacHdvRatios: { year: number; backcasting_ratio: number }[] | null = null;
        let emfacError: any = null;
        // Use EMFAC2025 for backcasting ratios
        for (const locName of countyVariations) {
          const { data, error } = await supabaseClient
            .from('emfac2025_county_filtered')
            .select('year, backcasting_ratio')
            .eq('location_name', locName)
            .eq('class', 'HDV')
            .in('year', [2005, 2006, 2007, 2008, 2009, 2010, 2011, 2012, 2013, 2014])
            .order('year', { ascending: true });
          if (!error && data && data.length > 0) {
            emfacHdvRatios = data as any;
            console.log('📊 VMT Comparison: Found EMFAC2025 HDV ratios for', locName, 'count:', data.length);
            break;
          }
          emfacError = error;
        }
        console.log('📊 VMT Comparison: EMFAC HDV backcasting ratios count:', emfacHdvRatios?.length || 0);
        if (!emfacHdvRatios && emfacError) {
          console.warn('📊 VMT Comparison: EMFAC HDV backcasting data not available:', emfacError);
        }
        
        // Backcast HDV for 2005-2014 using the ratios
        if (emfacHdvRatios && emfacHdvRatios.length > 0) {
          emfacHdvRatios.forEach(record => {
            if (record.backcasting_ratio) {
              const backcastedVmt = hdv2015 * record.backcasting_ratio;
              hdvByYear.set(record.year, backcastedVmt);
              console.log(`📊 VMT Comparison: HDV backcasting ${record.year}: ratio=${record.backcasting_ratio.toFixed(4)}, vmt=${backcastedVmt.toFixed(0)}`);
            }
          });
        }
        
        // Process data using yearMap
        const yearMap = new Map<number, { ldv?: number; hdv?: number }>();
        
        // Add LDV VMT data from the VMT chart (includes correct methodology: Replica 2023, AADT backcast 2015-2022, EMFAC backcast 2005-2014)
        vmtChartData.forEach(vmtRow => {
          yearMap.set(vmtRow.year, { ldv: vmtRow.vmt || 0 });
        });
        
        // Add HDV data (now includes backcasted data for 2005-2014)
        hdvByYear.forEach((vmt, year) => {
          if (!yearMap.has(year)) {
            yearMap.set(year, {});
          }
          const yearData = yearMap.get(year)!;
          yearData.hdv = vmt;
        });
        
        // Log the yearMap before conversion
        console.log('📊 VMT Comparison: yearMap entries:', Array.from(yearMap.entries()).map(([year, values]) => ({ year, ldv: values.ldv, hdv: values.hdv })));

        // Convert to array and sort by year
        let processedData: VMTDataPoint[] = Array.from(yearMap.entries())
          .map(([year, values]) => ({
            year,
            ldv: values.ldv || 0,
            hdv: values.hdv || 0,
            total: (values.ldv || 0) + (values.hdv || 0)
          }))
          .sort((a, b) => a.year - b.year);
        
        // Validate and fix data anomalies (e.g., dramatic drops that are likely data errors)
        processedData = processedData.map((point, index) => {
          // Check for anomalies in LDV data
          if (point.ldv > 0 && index > 0 && index < processedData.length - 1) {
            const prevPoint = processedData[index - 1];
            const nextPoint = processedData[index + 1];
            
            // If current point is less than 20% of previous and next, it's likely an error
            const prevAvg = (prevPoint.ldv + nextPoint.ldv) / 2;
            if (point.ldv < prevAvg * 0.2 && prevPoint.ldv > 0 && nextPoint.ldv > 0) {
              console.warn(`📊 VMT Comparison: Detected anomaly in ${point.year}: LDV=${point.ldv.toLocaleString()}, interpolating from adjacent years`);
              // Interpolate from adjacent years
              const interpolatedLDV = (prevPoint.ldv + nextPoint.ldv) / 2;
              return {
                ...point,
                ldv: interpolatedLDV,
                total: interpolatedLDV + point.hdv
              };
            }
          }
          return point;
        });
        
        console.log('📊 VMT Comparison: Processed data (after validation):', processedData);

        setData(processedData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching VMT comparison data:', err);
        setError('Failed to load VMT comparison data');
        setLoading(false);
      }
    };

    fetchData();
  }, [location, geofips]);

  useEffect(() => {
    if (data.length === 0 || !chartRef.current) return;

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    // Create new chart
    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: data.map(d => d.year.toString()),
        datasets: [
          {
            label: 'Light-Duty Vehicles (LDV)',
            data: data.map(d => d.ldv),
            borderColor: 'rgb(59, 130, 246)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(59, 130, 246)',
            pointBorderColor: 'rgb(59, 130, 246)'
          },
          {
            label: 'Heavy-Duty Vehicles (HDV)',
            data: data.map(d => d.hdv),
            borderColor: 'rgb(239, 68, 68)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            fill: true,
            tension: 0.4,
            borderWidth: 2,
            pointRadius: 4,
            pointHoverRadius: 6,
            pointBackgroundColor: 'rgb(239, 68, 68)',
            pointBorderColor: 'rgb(239, 68, 68)'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: standardLegend,
          title: standardTitle(`Light-Duty vs Heavy-Duty VMT - ${location}`),
          tooltip: {
            callbacks: {
              label: function(context) {
                const value = context.parsed.y;
                return `${context.dataset.label}: ${(value ?? 0).toLocaleString()} miles`;
              }
            }
          }
        },
        scales: {
          x: {
            title: {
              display: true,
              text: 'Year'
            }
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'VMT (miles)'
            },
            ticks: {
              callback: function(value) {
                return value.toLocaleString();
              }
            }
          }
        }
      }
    });

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [data]);

  if (loading) {
    return <div className="vmt-comparison-loading">Loading VMT comparison data...</div>;
  }

  if (error) {
    return <div className="vmt-comparison-error">{error}</div>;
  }

  if (data.length === 0) {
    return <div className="vmt-comparison-no-data">No VMT comparison data available</div>;
  }

  return (
    <div className="vmt-comparison-chart-container">
      <canvas ref={chartRef}></canvas>
    </div>
  );
};